import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { Project, CreateEndpointRequest } from '@mockd/shared';
import type { ProjectTemplate } from '@mockd/shared';
import { generateId, slugify } from '@mockd/shared/utils';
import { api, ApiError } from '../utils/apiClient';
import { addAnonymousProjectId } from '../utils/anonymousProjects';
import { useAuth } from './useAuth';

interface UseTemplateCreationReturn {
  createFromTemplate: (template: ProjectTemplate) => Promise<Project>;
  isCreating: boolean;
  loadingTemplateId: string | null;
}

export function useTemplateCreation(): UseTemplateCreationReturn {
  const { user } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);

  const createFromTemplate = useCallback(async (template: ProjectTemplate): Promise<Project> => {
    setIsCreating(true);
    setLoadingTemplateId(template.id);

    try {
      // Signed in, the project is saved to the account. Signed out, it's a
      // temporary one this browser can claim later (#257).
      let project: Project;
      if (user) {
        const subdomain = `${slugify(template.name)}-${generateId(6)}`;
        const projectJson = await api.post<{ data: Project }>('/api/projects', { name: template.name, subdomain });
        project = projectJson.data;
      } else {
        const projectJson = await api.post<{ data: Project }>('/api/projects/anonymous', { name: template.name });
        project = projectJson.data;
        addAnonymousProjectId(project.id);
      }

      // Create all endpoints from the template
      const results = await Promise.allSettled(
        template.endpoints.map((ep: CreateEndpointRequest) =>
          api.post(`/api/projects/${project.id}/endpoints`, ep)
        )
      );

      // A partial project is still usable, so return it — but say what's missing
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length === 0) {
        toast.success(`Created "${template.name}" with ${template.endpoints.length} endpoints`);
      } else {
        const reason = failures[0].reason;
        const detail = reason instanceof ApiError ? `: ${reason.message}` : '';
        toast.error(
          `Created "${template.name}", but ${failures.length} of ${template.endpoints.length} endpoints failed${detail}`
        );
      }

      return project;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to create from template';
      toast.error(msg);
      throw err;
    } finally {
      setIsCreating(false);
      setLoadingTemplateId(null);
    }
  }, [user]);

  return { createFromTemplate, isCreating, loadingTemplateId };
}
